import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useDropzone } from 'react-dropzone';
import * as XLSX from 'xlsx';
import { PlusIcon, TrashIcon, FunnelIcon } from '@heroicons/react/24/solid';
import GraphqlService from '../../../service/graphqlService';
import PageBanner from '../../../components/utils/PageBanner';
import { useSnackbar } from '../../../providers/SnackbarContext';
import YesNoDialog from '../../../components/utils/YesNoDialog';

export default function FineGrainedPermissions() {
  const queryClient = useQueryClient();
  const [newDevice, setNewDevice] = useState('');
  const [selectedBrand, setSelectedBrand] = useState(null);
  const openSnackBar = useSnackbar();
  const [yesNoDialogOpen, setYesNoDialogOpen] = useState(false);
  const [yesNoDialogText, setYesNoDialogText] = useState('');
  const [deviceToDelete, setDeviceToDelete] = useState(null);
  const [searchDevice, setSearchDevice] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [allowAllProjects, setAllowAllProjects] = useState(false);

  const getProjects = async () => GraphqlService.getProjects();

  const { data: projectsData } = useQuery(['getProjects'], () => getProjects(), {
    cacheTime: 1000,
  });

  const { data: devices, refetch } = useQuery(
    ['devicesPermissions', selectedBrand],
    () => GraphqlService.getDevicesPermissionsByProject(selectedBrand?.code),
    { enabled: !!selectedBrand },
  );

  // Effect to reset UI states when selectedBrand changes
  useEffect(() => {
    setSelectedDevices([]);
    setSearchDevice('');
    // allowAllProjects is managed by the useEffect depending on 'devices'
  }, [selectedBrand]);

  useEffect(() => {
    if (devices?.includes('*')) {
      setAllowAllProjects(true);
    } else {
      setAllowAllProjects(false);
    }
  }, [devices]);

  const createDeviceMutation = useMutation(
    (device) => GraphqlService.createDevicePermission(selectedBrand?.code, device),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['devicesPermissions', selectedBrand?.code]);
        setNewDevice('');
        openSnackBar('Device added successfully', 'success');
      },
    },
  );

  const deleteDeviceMutation = useMutation(
    (device) => GraphqlService.deleteDevicePermission(selectedBrand?.code, device),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['devicesPermissions', selectedBrand?.code]);
        openSnackBar('Device deleted successfully', 'success');
      },
    },
  );

  const handleCreateDevice = () => {
    createDeviceMutation.mutate(newDevice, {
      onSuccess: () => {
        refetch();
      },
    });
  };

  const handleDeleteDevice = (device) => {
    setDeviceToDelete(device);
    setYesNoDialogText(`Are you sure you want to delete the device ${device}?`);
    setYesNoDialogOpen(true);
  };

  const handleYesNoDialogAction = () => {
    if (deviceToDelete) {
      deleteDeviceMutation.mutate(deviceToDelete, {
        onSuccess: () => {
          refetch();
        },
      });
      setDeviceToDelete(null);
    } else {
      selectedDevices.forEach((device) => {
        deleteDeviceMutation.mutate(device, {
          onSuccess: () => {
            refetch();
          },
        });
      });
      setSelectedDevices([]);
    }
    setYesNoDialogOpen(false);
  };

  const handleSearchToggle = () => {
    setSearchOpen(!searchOpen);
  };

  const handleSelectAllClick = () => {
    if (devices?.filter(d => d !== '*').length === selectedDevices.length) {
      setSelectedDevices([]);
    } else {
      setSelectedDevices(devices.filter(d => d !== '*'));
    }
  };

  const handleSelectDevice = (device) => {
    const selectedIndex = selectedDevices.indexOf(device);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedDevices, device);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedDevices.slice(1));
    } else if (selectedIndex === selectedDevices.length - 1) {
      newSelected = newSelected.concat(selectedDevices.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedDevices.slice(0, selectedIndex),
        selectedDevices.slice(selectedIndex + 1),
      );
    }
    setSelectedDevices(newSelected);
  };

  const handleDeleteSelectedDevices = () => {
    setYesNoDialogText('Are you sure you want to delete the selected devices?');
    setYesNoDialogOpen(true);
  };

  const handleFileUpload = (acceptedFiles) => {
    const file = acceptedFiles[0];
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const json = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      const devicesToUpload = json.map((row) => String(row[0])).filter(Boolean);
      devicesToUpload.forEach((device) => {
        if (device.trim()) {
          createDeviceMutation.mutate(device.trim(), {
            onSuccess: () => {
              refetch();
            },
          });
        }
      });
    };
    reader.readAsArrayBuffer(file);
  };

  const handleAllowAllProjectsChange = (event) => {
    const isChecked = event.target.checked;
    setAllowAllProjects(isChecked);
    if (isChecked) {
      createDeviceMutation.mutate('*', {
        onSuccess: () => {
          openSnackBar('All devices allowed for this project', 'success');
          refetch();
        },
      });
    } else {
      deleteDeviceMutation.mutate('*', {
        onSuccess: () => {
          openSnackBar('This project is restricted to specific devices', 'success');
          refetch();
        },
      });
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv'],
    },
    onDrop: handleFileUpload,
  });

  const filteredDevices = devices?.filter((device) => device !== '*' && device.toLowerCase().includes(searchDevice.toLowerCase())) || [];

  return (
    <div className="min-h-screen bg-gray-100">
      <PageBanner title="FINE GRAINED PERMISSIONS" />
      <div className="p-6">
        <p className="mb-2 text-gray-700">
          Restrict Developer access to specific devices.
        </p>
        <p className="mb-6 text-gray-700">
          Please, add a Device Serial Number to allow developers’ visibility or upload a list.
        </p>
        <div className="bg-white p-6 rounded-lg shadow-md mb-6 max-w-xl">
          <div className="mb-4">
            <label htmlFor="project-select" className="block text-sm font-medium text-gray-700 mb-1">
              Select project
            </label>
            <select
              id="project-select"
              value={selectedBrand?.code || ''}
              onChange={(e) => {
                const selectedCodeValue = e.target.value;
                if (selectedCodeValue) {
                  setSelectedBrand(
                    projectsData?.find(
                      (project) => project.code === Number(selectedCodeValue)
                    )
                  );
                } else {
                  setSelectedBrand(null);
                }
              }}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md shadow-sm"
            >
              <option value="" disabled>Select a project</option>
              {projectsData
                ?.sort((a, b) => a.code - b.code)
                .map((project) => (
                  <option key={project.code} value={project.code}>
                    {`${project.code} - ${project.name}`}
                  </option>
                ))}
            </select>
          </div>

          <div className="flex space-x-2 mb-4">
            <input
              type="text"
              placeholder="Device Serial Number"
              value={newDevice}
              onChange={(e) => setNewDevice(e.target.value)}
              className="mt-1 flex-grow focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md p-2"
            />
            <button
              type="button"
              onClick={handleCreateDevice}
              disabled={!newDevice || !selectedBrand}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Add Device
            </button>
          </div>

          <div {...getRootProps()} className="mb-4 p-4 border-2 border-dashed border-gray-300 rounded-md cursor-pointer hover:border-blue-500 bg-gray-50 text-center">
            <input {...getInputProps()} />
            <button
              type="button"
              className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              disabled={!selectedBrand}
            >
              Upload File (XLS/XLSX/CSV)
            </button>
          </div>

          <div className="flex items-center mb-2">
            <label htmlFor="allowAllProjectsToggle" className="inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                id="allowAllProjectsToggle"
                className="sr-only peer"
                checked={allowAllProjects}
                onChange={handleAllowAllProjectsChange}
                disabled={!selectedBrand}
              />
              <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
              <span className="ms-3 text-sm font-medium text-gray-900 dark:text-gray-300">Allow All Serial Numbers</span>
            </label>
          </div>

          <p className="text-xs text-gray-500 mt-1">✔️ Upload your serial number list in CSV/XLSX format.</p>
          <p className="text-xs text-gray-500 mt-1">✔️ Include a single column with no header.</p>
        </div>

        {!allowAllProjects && selectedBrand && (
          <div className="bg-white p-0 rounded-lg shadow-md overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-center space-y-2 sm:space-y-0">
              <h3 className="text-lg font-semibold text-gray-900">DEVICE SERIAL NUMBERS</h3>
              <div className="flex items-center space-x-2">
                {searchOpen && (
                  <input
                    type="text"
                    placeholder="Search Device"
                    value={searchDevice}
                    onChange={(e) => setSearchDevice(e.target.value)}
                    className="block w-full sm:w-auto px-3 py-1.5 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                )}
                <button
                  type="button"
                  onClick={handleSearchToggle}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <FunnelIcon className="h-5 w-5 mr-1 text-gray-500" />
                  {searchOpen ? 'Close' : 'Search'}
                </button>
                <button
                  type="button"
                  onClick={handleDeleteSelectedDevices}
                  disabled={selectedDevices.length === 0}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                >
                  <TrashIcon className="h-5 w-5 mr-1" />
                  Delete Selected
                </button>
              </div>
            </div>
            <div className="overflow-x-auto scrollbar scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="p-4">
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        checked={filteredDevices.length > 0 && selectedDevices.length === filteredDevices.length}
                        onChange={handleSelectAllClick}
                        disabled={filteredDevices.length === 0}
                      />
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Serial Number
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredDevices.length === 0 && (
                    <tr>
                      <td colSpan="3" className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                        No devices found for this project or matching your search.
                      </td>
                    </tr>
                  )}
                  {filteredDevices.map((device) => (
                    <tr key={device} className={`${selectedDevices.indexOf(device) !== -1 ? 'bg-blue-50' : ''} hover:bg-gray-50`}>
                      <td className="p-4">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          checked={selectedDevices.indexOf(device) !== -1}
                          onChange={() => handleSelectDevice(device)}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{device}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          type="button"
                          onClick={() => handleDeleteDevice(device)}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                          <TrashIcon className="h-4 w-4 mr-1" />
                          DELETE
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {!selectedBrand && (
          <div className="bg-white p-6 rounded-lg shadow-md text-center text-gray-500">
            Please select a project to manage device permissions.
          </div>
        )}
        {selectedBrand && allowAllProjects && (
          <div className="bg-white p-6 rounded-lg shadow-md text-center text-green-600">
            All serial numbers are currently allowed for this project.
          </div>
        )}
      </div>
      <YesNoDialog
        open={yesNoDialogOpen}
        setOpen={setYesNoDialogOpen}
        header="Confirm Deletion"
        body={yesNoDialogText}
        action={handleYesNoDialogAction}
        actionName="Yes"
        id="confirmationDeleteDevice"
      />
    </div>
  );
}
